import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { shouldTrack, resetAdminCache, checkIsAdmin } from "@/lib/analytics-tracker";
import { pushEvent, setAnalyticsUserId } from "@/lib/gtm";
import { trackProduct, screenTemplate, setProductAnalyticsUser } from "@/lib/product-analytics";
import { isPrivatePath } from "@/seo/privatePaths";
import { trackBusinessEvent } from "@/lib/track-event";
import { sendOwnEvent, identifyOwnVisitor } from "@/lib/ownAnalytics";

// A page view in our own analytics (every real visitor; cookieless without
// consent). Campaign fields come from the attribution module (last non-direct
// touch), so an OAuth or Stripe return never overwrites them.
// A return from an OAuth provider (/api/...) carries a single-use code in its
// query string; that goes neither to the data layer nor to our analytics table.
const withoutSecrets = (pagePath: string) => (pagePath.startsWith("/api/") ? pagePath.split("?")[0] : pagePath);
const sendInternalPageview = (pagePath: string, pageTitle: string) =>
  sendOwnEvent("pageview", {}, { pagePath: withoutSecrets(pagePath), pageTitle, withSource: true });
const getContentType = (pathname: string): string => {
  if (pathname === '/' || pathname === '') return 'home';
  if (pathname.includes('/pricing')) return 'pricing';
  if (pathname.includes('/new-project') || pathname.includes('/newproject')) return 'newproject';
  if (pathname.includes('/product')) return 'product';
  if (pathname.includes('/dashboard')) return 'dashboard';
  if (pathname.includes('/auth')) return 'auth';
  if (pathname.includes('/admin')) return 'admin';
  if (pathname.includes('/settings')) return 'settings';
  if (pathname.includes('/project')) return 'project';
  if (pathname.includes('/analysis')) return 'analysis';
  if (pathname.includes('/blog')) return 'blog';
  if (pathname.includes('/contact')) return 'contact';
  if (pathname.includes('/about')) return 'about';
  // Default: use first path segment
  const segment = pathname.split('/').filter(Boolean)[0];
  return segment || 'other';
};

/**
 * Where on the page a click happened, in words a report can use: an explicit
 * data-section, the site header or footer, "hero" for the first section of the
 * page, otherwise the section's id or its position.
 */
const getCtaLocation = (el: HTMLElement): string => {
  const tagged = el.closest<HTMLElement>("[data-section]");
  if (tagged) return tagged.dataset.section || "section";
  if (el.closest("header, nav")) return "header";
  if (el.closest("footer")) return "footer";
  if (el.closest("[role='dialog']")) return "dialog";
  const section = el.closest("section");
  if (section) {
    const all = Array.from(document.querySelectorAll("main section, #root section"));
    const idx = all.indexOf(section);
    if (idx === 0) return "hero";
    return section.id || `section_${idx + 1}`;
  }
  return "body";
};

/** data-cta-text names a CTA whose visible text holds more than its label (a sub line, an offer). */
const getCtaText = (el: HTMLElement): string =>
  (el.dataset.ctaText || el.innerText || el.getAttribute("aria-label") || el.getAttribute("title") || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);

/** Pages a click is worth reporting for: the ones that make or lead to money. */
const CONVERSION_PATH = /^\/(?:(?:en|el)\/)?(signup|login|auth|free-audit|free-cro[\w-]*|pricing|checkout|contact|book[\w-]*|demo)(\/|$)/;
const stripLang = (path: string) => path.replace(/^\/(en|el)(?=\/|$)/, "") || "/";

/**
 * TrackingManager: the app's side of measurement.
 *
 * GA4 (through GTM) gets a short fixed list of events, all via pushEvent():
 *   virtual_pageview  once per landing and once per route (pathname) change
 *   cta_click         a click that heads to a conversion page, or on [data-track]
 *   login             a real sign-in, once per user per tab session
 * sign_up, generate_lead and begin_checkout are pushed where they happen.
 *
 * Consent is restored before GTM loads (index.html); bots, previews and
 * internal devices never load GTM at all, and pushEvent() is a no-op for them.
 * Our own analytics (track-analytics) follows the same verdict via shouldTrack().
 */
// Global flag to prevent duplicate initial tracking in React.StrictMode (development)
let trackingInitialized = false;

/** A click on a button that routes in code has no href; remember it briefly. */
type PendingClick = { ts: number; text: string; location: string; id?: string };

export const TrackingManager = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const isInitialized = useRef(false);
  const hasRunInitialSetup = useRef(false);
  const pendingClick = useRef<PendingClick | null>(null);
  const lastCta = useRef<{ ts: number; key: string }>({ ts: 0, key: "" });

  const pageviewPayload = (path: string) => ({
    page_path: path,
    // On an OAuth return the address bar may still hold the code when this runs (the page's chunk loads lazily).
    page_location: path.startsWith("/api/") ? window.location.origin + path : window.location.href,
    page_title: document.title,
    platform_type: "Website",
    content_type: getContentType(location.pathname),
    language,
  });

  // Identity stitching + login on auth state change
  const hasTrackedLogin = useRef(false);
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === "SIGNED_IN" || event === "INITIAL_SESSION") && session?.user && session.access_token) {
        resetAdminCache();
        // GA user id (a uuid, never the email) for cross-device reporting.
        setAnalyticsUserId(session.user.id);
        setProductAnalyticsUser(session.user.id);

        if (shouldTrack()) {
          // Marks admins as an internal device (no GA from here on), then
          // links this browser to the account in our own analytics (consent only).
          void checkIsAdmin().then(() => identifyOwnVisitor(session.access_token));
        }

        /*
         * SIGNED_IN is a real sign-in, but supabase-js also re-emits it when a
         * tab regains focus, and the 2FA flow signs in twice. One login per
         * user per tab session. Accounts created in the last 15 minutes are
         * reported as sign_up by onboarding instead, not as a login.
         */
        if (event === "SIGNED_IN" && !hasTrackedLogin.current) {
          hasTrackedLogin.current = true;
          const user = session.user;
          const key = `ec_login:${user.id}`;
          let seen = false;
          try { seen = !!sessionStorage.getItem(key); sessionStorage.setItem(key, "1"); } catch { /* ignore */ }
          const createdMs = Date.parse(user.created_at || "");
          const isNew = Number.isFinite(createdMs) && Date.now() - createdMs < 15 * 60 * 1000;
          if (!seen) {
            const method = (user.app_metadata?.provider as string | undefined) || "email";
            trackBusinessEvent("login", { method });
            if (!isNew) pushEvent("login", { method });
          }
        }
      }
      if (event === "SIGNED_OUT") {
        setAnalyticsUserId(null);
        setProductAnalyticsUser(null);
        hasTrackedLogin.current = false;
        resetAdminCache();
        trackBusinessEvent("logout");
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  /*
   * CTA clicks. Not every click: only links and buttons that lead to a page
   * where money is made (signup, free audit, pricing, checkout, contact), or
   * elements explicitly tagged with data-track="name". Outbound links are left
   * to GA4 enhanced measurement. App pages (workspace, settings, reports) are
   * product usage and are measured in our own product_events instead.
   */
  useEffect(() => {
    const emit = (cta: { text: string; location: string; id?: string }, destination: string) => {
      const key = `${cta.id || cta.text}|${destination}`;
      const now = performance.now();
      if (lastCta.current.key === key && now - lastCta.current.ts < 1000) return;
      lastCta.current = { ts: now, key };
      pushEvent("cta_click", {
        cta_text: cta.text,
        cta_location: cta.location,
        cta_destination: destination,
        cta_id: cta.id,
      });
      if (cta.location === "hero") {
        trackBusinessEvent("hero_cta_clicked", { cta_text: cta.text, page: window.location.pathname, href: destination });
      }
    };

    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (isPrivatePath(stripLang(window.location.pathname))) {
        // In the app, only elements tagged data-track="name" are recorded,
        // as product usage (product_events), not as GA events.
        const tagged = target?.closest<HTMLElement>("[data-track]");
        if (tagged?.dataset.track) {
          trackProduct("ui_action", {
            id: tagged.dataset.track,
            text: getCtaText(tagged),
            screen: screenTemplate(window.location.pathname),
          });
        }
        return;
      }
      const el = target?.closest<HTMLElement>("a[href], button, [data-track]");
      if (!el) return;
      const cta = {
        text: getCtaText(el),
        location: getCtaLocation(el),
        id: el.closest<HTMLElement>("[data-track]")?.dataset.track,
      };

      if (el.tagName === "A") {
        let url: URL;
        try { url = new URL((el as HTMLAnchorElement).href, window.location.href); } catch { return; }
        if (url.origin !== window.location.origin) return; // outbound: GA4 enhanced measurement
        const dest = url.pathname;
        if (cta.id || CONVERSION_PATH.test(dest)) emit(cta, dest);
        return;
      }
      if (cta.id) {
        emit(cta, "");
        return;
      }
      // A button that navigates in code: decide once the route changes.
      pendingClick.current = { ts: performance.now(), ...cta };
    };

    // Enter in a form (e.g. the hero URL field) routes without a click.
    const onSubmit = (event: SubmitEvent) => {
      if (isPrivatePath(stripLang(window.location.pathname))) return;
      const form = event.target as HTMLElement | null;
      if (!form) return;
      const submitter = (event.submitter as HTMLElement | null) || form.querySelector<HTMLElement>("button[type='submit']");
      pendingClick.current = {
        ts: performance.now(),
        text: submitter ? getCtaText(submitter) : "form submit",
        location: getCtaLocation(form),
        id: form.closest<HTMLElement>("[data-track]")?.dataset.track,
      };
    };

    document.addEventListener("click", onClick, true);
    document.addEventListener("submit", onSubmit, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("submit", onSubmit, true);
    };
  }, []);

  // Initial tracking setup - runs only once globally
  useEffect(() => {
    isInitialized.current = true;
    if (trackingInitialized || hasRunInitialSetup.current) return;
    hasRunInitialSetup.current = true;
    trackingInitialized = true;


    // Delay so react-helmet has set document.title
    const timeoutId = setTimeout(() => {
      const path = withoutSecrets(location.pathname + location.search);
      pushEvent("virtual_pageview", pageviewPayload(path));
      void sendInternalPageview(path, document.title);
      if (isPrivatePath(stripLang(location.pathname))) {
        trackProduct("screen_viewed", { screen: screenTemplate(location.pathname) });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * One page_view per pathname change. Query-only changes (wizard steps, blog
   * filters, ?tab=) are the same page and used to inflate page views.
   */
  const previousPathname = useRef(location.pathname);
  useEffect(() => {
    if (!isInitialized.current) return;
    if (location.pathname === previousPathname.current) return;
    const fromPath = previousPathname.current;
    previousPathname.current = location.pathname;

    // A code-routed button click that landed on a conversion page is a CTA click.
    const pending = pendingClick.current;
    pendingClick.current = null;
    if (pending && performance.now() - pending.ts < 1500 && (pending.id || CONVERSION_PATH.test(location.pathname))) {
      const key = `${pending.id || pending.text}|${location.pathname}`;
      if (!(lastCta.current.key === key && performance.now() - lastCta.current.ts < 1000)) {
        lastCta.current = { ts: performance.now(), key };
        pushEvent("cta_click", {
          cta_text: pending.text,
          cta_location: pending.location,
          cta_destination: location.pathname,
          cta_id: pending.id,
        });
        if (pending.location === "hero") {
          trackBusinessEvent("hero_cta_clicked", { cta_text: pending.text, page: fromPath, href: location.pathname });
        }
      }
    }

    const timeoutId = setTimeout(() => {
      const path = withoutSecrets(location.pathname + location.search);
      pushEvent("virtual_pageview", pageviewPayload(path));
      void sendInternalPageview(path, document.title);
      if (isPrivatePath(stripLang(location.pathname))) {
        trackProduct("screen_viewed", { screen: screenTemplate(location.pathname) });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ── Scroll depth (own analytics only), public pages ─────────────
  useEffect(() => {
    if (isPrivatePath(stripLang(location.pathname))) return;
    const thresholds = [25, 50, 75, 100];
    const fired = new Set<number>();

    const handleScroll = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const percent = Math.round((window.scrollY / docHeight) * 100);
      for (const t of thresholds) {
        if (percent >= t && !fired.has(t)) {
          fired.add(t);
          trackBusinessEvent("scroll_depth", { percent: t, page: location.pathname });
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  return null;
};

// Type declarations
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}
